import '../GenerateQuestionDialog/GenerateQuestion.scss';

import * as React from 'react';

import { AiJobStatusResponse, AiQuestion } from '~api/ai/types';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { translate } from '~l10n/client';
import { Badge } from '~shared/components/Badge';
import { IconButton, SmallButton } from '~shared/components/buttons';
import { IconChevron } from '~shared/components/icons/large';
import { useQuizContext } from '~teacher/components/QuizEditor/contexts/QuizContextProvider';
import { LearnMoreIcon } from '~teacher/components/QuizEditor/GenerateQuestionDialog/LearnMoreIcon';
import { QuestionDetails } from '~teacher/components/QuizEditor/GenerateQuestionDialog/QuestionDetails';
import {
  useAddAllQuestions,
  useAddQuestion,
} from '~teacher/components/QuizEditor/shared/AiQuestions';
import { addQuestionIds } from '~teacher/polling/utils';

import {
  extractAddedQuestionSetAtom,
  extractQuestionsPositionAtom,
} from './atoms';
import { ExtractQuestionsError } from './ExtractQuestionsError';

interface ExtractQuestionsResultsProps {
  pollData: AiJobStatusResponse['data'];
  isFailed: boolean;
  isTimeout: boolean;
  cleanup: () => void;
  onClose: () => void;
  onBack: () => void;
  refreshQuizData: () => void;
  handleExtract: () => void;
}

export function ExtractQuestionsResults({
  pollData,
  isFailed,
  isTimeout,
  cleanup,
  onClose,
  onBack,
  refreshQuizData,
  handleExtract,
}: ExtractQuestionsResultsProps) {
  const quizContext = useQuizContext();
  const resetAddedQuestions = useSetAtom(extractAddedQuestionSetAtom.resetAtom);
  const addToAddedQuestionSet = useSetAtom(extractAddedQuestionSetAtom.addAtom);
  const addedQuestionSet = useAtomValue(extractAddedQuestionSetAtom.valueAtom);
  const [position] = useAtom(extractQuestionsPositionAtom);

  const displayData = React.useMemo(() => {
    if (pollData?.questions) {
      return {
        ...pollData,
        questions: addQuestionIds(pollData.questions),
      };
    }
    return null;
  }, [pollData]);

  const { addAllQuestions, isAddingAllQuestions } = useAddAllQuestions({
    quizId: quizContext.quizId,
    position,
    questions: displayData?.questions,
    addedQuestionIds: addedQuestionSet,
    onQuestionAdded: addToAddedQuestionSet,
    onSuccess: async () => {
      cleanup();
      refreshQuizData();
      onClose();
    },
    onError: () => refreshQuizData(),
    uiSource: 'ai_extract',
  });

  const handleBack = () => {
    cleanup();
    resetAddedQuestions();
    onBack();
  };

  const hasQuestions =
    displayData?.questions && displayData.questions.length > 0;

  return (
    <>
      <div className="pb-4">
        <div className="flex gap-4">
          <div className="flex flex-col">
            <IconButton aria-label={translate('Back')} onClick={handleBack}>
              <IconChevron direction="left" />
            </IconButton>
          </div>
          <div className="flex flex-shrink flex-col">
            <div className="flex items-center gap-2">
              <a
                href="https://help.socrative.com/en/articles/8732134-socrative-ai-question-generation"
                target="_blank"
                rel="noopener noreferrer"
                className="no-underline"
              >
                <Badge textTransform="capitalize" tone="ai">
                  {translate('AI Assisted')}
                  <LearnMoreIcon />
                </Badge>
              </a>
              <Badge textTransform="uppercase" tone="beta">
                {translate('Beta')}
              </Badge>
            </div>
            <div className="font-display text-md leading-8 text-blue-900 font-700">
              {translate('Extract Questions from Document')}
            </div>
            <div className="text-sans text-base leading-8 text-blue-600">
              {translate(
                'Add questions to your quiz. When you are done, edit the questions on your quiz.'
              )}
            </div>
          </div>
        </div>
      </div>
      <div
        className="flex flex-col h-full justify-between min-h-0"
        style={{
          boxShadow: '0px 1px 0px 0px #E7EDF0 inset',
        }}
      >
        <ExtractQuestionsResultsContent
          questions={displayData?.questions}
          isFailed={isFailed}
          isTimeout={isTimeout}
          quizId={quizContext.quizId}
          handleExtract={handleExtract}
        />
        <div
          style={{
            boxShadow: '0px 1px 0px 0px #E7EDF0 inset',
          }}
          className="flex justify-end items-center gap-3 pt-5"
        >
          <SmallButton
            isDisabled={isAddingAllQuestions || !hasQuestions}
            appearance="ghost"
            onClick={() => {
              addAllQuestions();
            }}
          >
            {translate('Add All Questions')}
          </SmallButton>
          <SmallButton
            appearance="primary"
            onClick={() => {
              cleanup();
              onClose();
            }}
          >
            {translate('Done')}
          </SmallButton>
        </div>
      </div>
    </>
  );
}

function ExtractQuestionsResultsContent({
  questions,
  isFailed,
  isTimeout,
  quizId,
  handleExtract,
}: {
  questions: AiQuestion[] | undefined;
  isFailed: boolean;
  isTimeout: boolean;
  quizId: number;
  handleExtract: () => void;
}) {
  if (questions && questions.length > 0) {
    return <ExtractQuestionsSuccess questions={questions} quizId={quizId} />;
  }

  if (isFailed || isTimeout) {
    return <ExtractQuestionsError onRetry={handleExtract} />;
  }

  return null;
}

function ExtractQuestionsSuccess({
  questions,
  quizId,
}: {
  questions: AiQuestion[];
  quizId: number;
}) {
  return (
    <div className="flex flex-col min-h-0 flex-shrink">
      <div className="overflow-y-scroll overflow-x-hidden min-h-0 flex-shrink">
        {questions.map((question) => (
          <ExtractQuestionCard
            key={question.questionId}
            quizId={quizId}
            question={question}
          />
        ))}
      </div>
      <div
        className="text-center text-sans text-xs text-blue-600 leading-6 py-8"
        style={{ boxShadow: '0px 1px 0px 0px #E7EDF0 inset' }}
      >
        {translate(
          'Extracted questions can make mistakes. Consider checking question accuracy.'
        )}
      </div>
    </div>
  );
}

function ExtractQuestionCard({
  question,
  quizId,
}: {
  question: AiQuestion;
  quizId: number;
}) {
  const [position] = useAtom(extractQuestionsPositionAtom);
  const [addedQuestionSet] = useAtom(extractAddedQuestionSetAtom.valueAtom);
  const addToAddedQuestionSet = useSetAtom(extractAddedQuestionSetAtom.addAtom);
  const isQuestionAdded = addedQuestionSet.has(question.questionId);

  const { addQuestion } = useAddQuestion({
    quizId,
    position,
    onQuestionAdded: addToAddedQuestionSet,
  });

  return (
    <div className="question-card">
      <div className="flex">
        <div className="flex-grow">
          <QuestionDetails
            questionType={question.questionType}
            questionText={question.questionText}
            answers={question.answers}
            explanation={question.explanation}
          />
        </div>
        <div className="flex justify-end pt-5 flex-shrink-0 flex-grow-0">
          <div className="flex pr-2">
            <div>
              <SmallButton
                appearance="primary"
                isDisabled={isQuestionAdded}
                onClick={() =>
                  addQuestion({
                    questionType: question.questionType,
                    questionText: question.questionText,
                    answers: question.answers,
                    explanation: question.explanation,
                    questionId: question.questionId,
                  })
                }
              >
                {translate('Add')}
              </SmallButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
